import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import {
  FilterGroupingEnum,
  Task,
  TaskKindEnum,
  TaskStatusEnum,
} from 'graphql-common';
import _get from 'lodash/get';
import Collapse, { CollapseCropColor } from '@lib/components/Collapse/Collapse';
import AppContentMaxWidth from '@lib/layouts/AppLayout/AppContentMaxWidth/AppContentMaxWidth';
import PageBody from '@lib/layouts/DashboardLayout/PageBody/PageBody';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import { ErrorLike } from '@apollo/client';
import Typography from '@lib/components/Typography/Typography';
import MaterialIcon from '@lib/components/MaterialIcon/MaterialIcon';
import Chip, { ChipTypes } from '@lib/components/Chip/Chip';
import { colorToChipType } from '@lib/components/Chip/utils/colorEnum';
import { getFormattedDate } from '@lib/utils/dateTimeHelpers';
import AssetPreview from '@lib/components/AssetPreview/AssetPreview';
import ErrorPage from '@lib/components/ErrorPage/ErrorPage';
import useIsMobileDevice from '@lib/hooks/useIsMobileDevice';
import CountMark, {
  CountMarkSizes,
  CountMarkTypes,
} from '@lib/components/CountMark/CountMark';
import getStatusFromError from '@lib/utils/getStatusFromError';
import getDescriptionFromError from '@lib/utils/getDescriptionFromError';
import useIsOnline from '@lib/hooks/useIsOnline';
import { getPriorityChipLabel, getPriorityChipType } from '@lib/utils/priority';
import LinkedDocuments from '@lib/components/LinkedDocuments';
import TaskScopeChip from 'components/TaskScopeChip/TaskScopeChip';
import TaskViewAction from 'components/Task/TaskViewAction';
import isDocumentViewUrl from 'components/Task/isDocumentViewUrl';
import entityViewStyles from '@lib/assets/styles/EntityView.module.scss';
import SyncLabel from '@lib/components/SyncLabel/SyncLabel';
import TopNavigationHeader from '@lib/layouts/AppLayout/TopNavigationHeader/TopNavigationHeader';
import { APP_URLS } from 'constants/urls';
import TextEditor from '@lib/components/TextEditor/TextEditor';
import { hasTextEditorValue } from '@lib/components/TextEditor/utils';

const { VITE_BLOCK_UI_BY_DEVICE } = import.meta.env;
const shouldBlockUiByDevice = VITE_BLOCK_UI_BY_DEVICE === 'true';

interface Props {
  isFormAvailable?: boolean;
  isFormDisabled?: boolean;
  isSynced?: boolean;
  isViewLocked?: boolean;
  taskData: Task | null;
  taskError?: ErrorLike;
  taskLoading?: boolean;
  isTaskTimeoutError?: boolean;
}

function TaskView(props: Props) {
  const {
    isFormAvailable,
    isFormDisabled,
    isSynced,
    isViewLocked,
    taskData,
    taskError,
    taskLoading = false,
    isTaskTimeoutError,
  } = props;
  const isOnline = useIsOnline();
  const { t } = useTranslation();
  const isMobileDevice = useIsMobileDevice();
  const [isBlockedUI, setBlockedUI] = useState(
    shouldBlockUiByDevice ? !isMobileDevice : false,
  );
  const {
    asset,
    completedAt,
    deadlineAt,
    description,
    formTemplate,
    groupKey,
    id = '',
    kind,
    lastCheckAt,
    lastDeadlineAt,
    lateFor,
    name,
    priority,
    site,
    siteArea,
    status,
  } = taskData || {};
  const categoryName = _get(formTemplate, 'category.name');
  const categoryColor = colorToChipType(_get(formTemplate, 'category.color'));

  const isOffline = siteArea?.poorWifi || asset?.poorWifi;

  useEffect(() => {
    if (shouldBlockUiByDevice) {
      setBlockedUI(!isMobileDevice);
    }
  }, [isMobileDevice]);

  if (taskError && isOnline && !taskLoading) {
    return (
      <ErrorPage
        status={getStatusFromError(taskError)}
        description={getDescriptionFromError(taskError)}
      />
    );
  }

  if (taskData === null && (!isOnline || isTaskTimeoutError) && !taskLoading) {
    return <ErrorPage status={503} />;
  }
  if (taskData === null) {
    return null;
  }

  return (
    <>
      <TopNavigationHeader
        title={t('task-details')}
        backUrl={APP_URLS.app.tasks.index.getPathWithQuery()}
        predictorFnUsePrevUrl={({ prevUrl }) =>
          prevUrl !==
            APP_URLS.app.tasks.completionView.getDynamicPath({
              pathParts: { id },
            }) &&
          prevUrl !==
            APP_URLS.app.tasks.completionCreate.getDynamicPath({
              pathParts: { id },
            }) &&
          !isDocumentViewUrl(prevUrl)
        }
      />
      <PageBody withoutHeader>
        <div>
          {taskLoading ? (
            <CircleLoader />
          ) : (
            <AppContentMaxWidth>
              {categoryColor && categoryName && (
                <div className={entityViewStyles.topHeader}>
                  <Chip
                    type={categoryColor}
                    label={categoryName}
                    leftIcon="circle"
                  />
                  {isOffline && status !== TaskStatusEnum.Completed && (
                    <SyncLabel isSynced={!!isSynced} />
                  )}
                </div>
              )}
              <div className={entityViewStyles.header}>
                <Collapse minHeight={120} cropColor={CollapseCropColor.White}>
                  <Typography
                    variant="h2"
                    className={entityViewStyles.title}
                    data-test-id="task-view__task-name"
                  >
                    {name}
                  </Typography>
                </Collapse>
              </div>
              <div className={entityViewStyles.infoList}>
                <div className={entityViewStyles.infoItem}>
                  <p className={entityViewStyles.infoItemLabel}>
                    <MaterialIcon icon="segment" symbolsOutlined />
                    <Typography variant="caption">{t('priority')}</Typography>
                  </p>
                  <div
                    className={entityViewStyles.infoItemContent}
                    data-test-id="task-view__priority-chip"
                  >
                    {priority ? (
                      <Chip
                        type={getPriorityChipType(priority)}
                        label={getPriorityChipLabel(priority)}
                        leftIcon="circle"
                      />
                    ) : (
                      <Typography variant="body">-</Typography>
                    )}
                  </div>
                </div>
                <div className={entityViewStyles.infoItem}>
                  <p className={entityViewStyles.infoItemLabel}>
                    <MaterialIcon icon="track_changes" symbolsOutlined />
                    <Typography variant="caption">{t('status')}</Typography>
                  </p>
                  <div
                    className={entityViewStyles.infoItemContent}
                    data-test-id="task-view__status-chip"
                  >
                    {status ? (
                      <TaskScopeChip task={taskData} />
                    ) : (
                      <Typography variant="body">-</Typography>
                    )}
                  </div>
                </div>
                <div className={entityViewStyles.infoItem}>
                  <p className={entityViewStyles.infoItemLabel}>
                    <MaterialIcon icon="home_pin" symbolsOutlined />
                    <Typography variant="caption">
                      {t('site-comma-area')}
                    </Typography>
                  </p>
                  <div
                    className={classNames(
                      entityViewStyles.infoItemContent,
                      entityViewStyles.infoItemContentRow,
                    )}
                  >
                    {site ? (
                      <Chip
                        type={ChipTypes.chipGray}
                        label={_get(site, 'name')}
                      />
                    ) : (
                      <Typography variant="body">-</Typography>
                    )}
                    {siteArea ? (
                      <Chip
                        type={ChipTypes.chipGray}
                        label={_get(siteArea, 'name')}
                      />
                    ) : (
                      <Typography variant="body">-</Typography>
                    )}
                  </div>
                </div>
                {kind === TaskKindEnum.OnDemand ? (
                  <div className={entityViewStyles.infoItem}>
                    <p className={entityViewStyles.infoItemLabel}>
                      <MaterialIcon icon="history" symbolsOutlined />
                      <Typography variant="caption">
                        {t('last-completion')}
                      </Typography>
                    </p>
                    <div className={entityViewStyles.infoItemContent}>
                      {status === TaskStatusEnum.Completed ? (
                        <Typography variant="body">
                          {completedAt
                            ? getFormattedDate(completedAt as string)
                            : '-'}
                        </Typography>
                      ) : (
                        <Typography variant="body">
                          {lastCheckAt
                            ? getFormattedDate(lastCheckAt as string)
                            : '-'}
                        </Typography>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className={entityViewStyles.infoItem}>
                    <p className={entityViewStyles.infoItemLabel}>
                      <MaterialIcon icon="history" symbolsOutlined />
                      <Typography variant="caption">
                        {t('last-task-order')}
                      </Typography>
                    </p>
                    <div className={entityViewStyles.infoItemContent}>
                      <Typography variant="body">
                        {lastDeadlineAt
                          ? getFormattedDate(lastDeadlineAt as string)
                          : '-'}
                      </Typography>
                    </div>
                  </div>
                )}
                {status === TaskStatusEnum.Completed &&
                  kind !== TaskKindEnum.OnDemand && (
                    <div className={entityViewStyles.infoItem}>
                      <p className={entityViewStyles.infoItemLabel}>
                        <MaterialIcon
                          icon="assignment_turned_in"
                          symbolsOutlined
                        />
                        <Typography variant="caption">
                          {t('completion-date')}
                        </Typography>
                      </p>
                      <div className={entityViewStyles.infoItemContent}>
                        <Typography variant="body">
                          {completedAt
                            ? getFormattedDate(completedAt as string)
                            : '-'}
                        </Typography>
                      </div>
                    </div>
                  )}
                {kind !== TaskKindEnum.OnDemand &&
                  status !== TaskStatusEnum.Completed &&
                  status !== TaskStatusEnum.Archived && (
                    <div className={entityViewStyles.infoItem}>
                      <p className={entityViewStyles.infoItemLabel}>
                        <MaterialIcon icon="update" symbolsOutlined />
                        <Typography variant="caption">
                          {t('next-task-order')}
                        </Typography>
                      </p>
                      <div className={entityViewStyles.infoItemContent}>
                        <Typography variant="body">
                          {deadlineAt
                            ? getFormattedDate(deadlineAt as string)
                            : '-'}
                        </Typography>
                      </div>
                    </div>
                  )}
                {lateFor && (
                  <div className={entityViewStyles.infoItem}>
                    <p className={entityViewStyles.infoItemLabel}>
                      <MaterialIcon icon="clock_loader_20" symbolsOutlined />
                      <Typography variant="caption">{t('late-for')}</Typography>
                    </p>
                    <div className={entityViewStyles.infoItemContent}>
                      <CountMark
                        value={`${lateFor} ${t('day-short')}`}
                        type={CountMarkTypes.red}
                        size={CountMarkSizes.large}
                      />
                    </div>
                  </div>
                )}
                <div className={entityViewStyles.infoItem}>
                  <p className={entityViewStyles.infoItemLabel}>
                    <MaterialIcon icon="inventory" symbolsOutlined />
                    <Typography variant="caption">{t('asset')}</Typography>
                  </p>
                  <div className={entityViewStyles.infoItemContent}>
                    {asset ? (
                      <AssetPreview
                        name={asset.name}
                        code={asset?.code}
                        icon={
                          asset.iconAttached?.urls?.small ||
                          asset.iconAttached?.urls?.original ||
                          asset.iconAttached?.fileTag
                        }
                      />
                    ) : (
                      <Typography variant="body">-</Typography>
                    )}
                  </div>
                </div>
                {hasTextEditorValue(description) && (
                  <div className={entityViewStyles.infoItemCol}>
                    <p className={entityViewStyles.infoItemTitle}>
                      <MaterialIcon icon="description" symbolsOutlined />
                      <Typography variant="caption">
                        {t('description')}
                      </Typography>
                    </p>
                    <div className={entityViewStyles.infoItemContent}>
                      <TextEditor
                        readOnly
                        value={description}
                        className={entityViewStyles.textEditor}
                      />
                    </div>
                  </div>
                )}
                {groupKey && !isViewLocked && (
                  <LinkedDocuments
                    icon="attach_file"
                    label={t('task-related-documents')}
                    filters={[
                      {
                        documentationTaskGroupsGroupKey: {
                          grouping: FilterGroupingEnum.And,
                          predicate: { in: [groupKey] },
                        },
                      },
                    ]}
                    getDetailsUrl={(doc) =>
                      APP_URLS.app.documentation.view.getDynamicPath({
                        pathParts: { id: doc.id },
                      })
                    }
                    useFavoritesTool
                    useMarkAsReadTool
                    showUnavailableState
                  />
                )}
              </div>
              <div>
                <TaskViewAction
                  disabled={isBlockedUI}
                  isFormDisabled={isFormDisabled}
                  isFormAvailable={isFormAvailable}
                  taskData={taskData}
                />
              </div>
            </AppContentMaxWidth>
          )}
        </div>
      </PageBody>
    </>
  );
}

export default TaskView;
